import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authorizedFetch } from "../auth/authorizedFetch";
import { throwIfNotOk } from "./errorEnvelope";
import type {
  AccessRequest,
  ClientDataGrant,
  GrantAccessRequest,
  GrantAccessResult,
  RevokeAccessResult,
} from "./types";

const API_BASE = import.meta.env.VITE_KEYCLOAK_API_URL ?? "/keycloak-api";
const FGA_API_BASE = import.meta.env.VITE_FGA_API_URL || "/fga-api/api";

// The client's stored data grants, read from the access store (ADR-0040). Takes the
// readable clientId (the FGA subject is client:<clientId>), not the Keycloak UUID.
// The result is bounded by the viewer's authority scope on fga-api.
export function useClientAccess(fgaClientId: string) {
  return useQuery({
    queryKey: ["grants", `client:${fgaClientId}`],
    enabled: !!fgaClientId,
    queryFn: async () => {
      const res = await authorizedFetch(
        `${FGA_API_BASE}/grants?subject=${encodeURIComponent(`client:${fgaClientId}`)}`,
      );
      await throwIfNotOk(res, "fga");
      const body = (await res.json()) as { grants: ClientDataGrant[] };
      return body.grants;
    },
  });
}

export function useGrantAccess(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: GrantAccessRequest) => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${clientId}/data-access`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      // 200 = granted, 202 = queued for approval. Both are non-error.
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<GrantAccessResult>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants"] });
      queryClient.invalidateQueries({
        queryKey: ["clients", clientId, "requests"],
      });
    },
  });
}

export function useRevokeAccess(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: GrantAccessRequest) => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${clientId}/data-access`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<RevokeAccessResult>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants"] });
    },
  });
}

export function useClientRequests(clientId: string) {
  return useQuery({
    queryKey: ["clients", clientId, "requests"],
    enabled: !!clientId,
    queryFn: async () => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${clientId}/requests`,
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<AccessRequest[]>;
    },
  });
}

export function usePendingRequests() {
  return useQuery({
    queryKey: ["requests", "pending"],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_BASE}/requests/pending`);
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<AccessRequest[]>;
    },
    staleTime: 1000 * 30,
  });
}

export function useDecideRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      decision,
      reason,
    }: {
      id: string;
      decision: "approve" | "deny";
      reason?: string;
    }) => {
      const res = await authorizedFetch(
        `${API_BASE}/requests/${id}/${decision}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: decision === "deny" ? JSON.stringify({ reason }) : undefined,
        },
      );
      await throwIfNotOk(res, "kc");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["requests", "pending"] });
    },
  });
}

export function clientAudienceGrantsQuery(clientId: string) {
  return {
    queryKey: ["clients", clientId, "audience-grants"],
    enabled: !!clientId,
    queryFn: async () => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${clientId}/api-access`,
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<{ audience: string }[]>;
    },
  };
}

export function useClientAudienceGrants(clientId: string) {
  return useQuery(clientAudienceGrantsQuery(clientId));
}

export function useAddAudienceGrant(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { audience: string }) => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${clientId}/api-access`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      await throwIfNotOk(res, "kc");
      if (res.status === 204)
        return { requiresApproval: false } as GrantAccessResult;
      return res.json() as Promise<GrantAccessResult>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clients", clientId, "audience-grants"],
      });
      queryClient.invalidateQueries({
        queryKey: ["clients", clientId, "requests"],
      });
    },
  });
}

export function useRemoveAudienceGrant(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (audience: string) => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${clientId}/api-access/${encodeURIComponent(audience)}`,
        { method: "DELETE" },
      );
      await throwIfNotOk(res, "kc");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clients", clientId, "audience-grants"],
      });
    },
  });
}

export function useCancelRequest(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authorizedFetch(`${API_BASE}/requests/${id}`, {
        method: "DELETE",
      });
      await throwIfNotOk(res, "kc");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clients", clientId, "requests"],
      });
    },
  });
}
